/**
 * Authentication: signing in and out, the signed-in caller (`/v1/me`), and
 * redeeming an invitation.
 */

import { request } from './client.js';
import type { Company } from './company.js';

export interface LoginSuccess {
  readonly user_id: string;
  readonly company_id: string;
  readonly expires_at: string;
}

export interface CompanyChoiceRequired {
  readonly requires_company_choice: true;
  readonly companies: readonly Company[];
}

export type LoginResponse = LoginSuccess | CompanyChoiceRequired;

export function isCompanyChoice(r: LoginResponse): r is CompanyChoiceRequired {
  return 'requires_company_choice' in r;
}

export function login(
  email: string,
  password: string,
  companyId?: string,
): Promise<LoginResponse> {
  return request<LoginResponse>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, ...(companyId ? { company_id: companyId } : {}) }),
  });
}

export function logout(): Promise<void> {
  return request<void>('/v1/auth/logout', { method: 'POST' });
}

export interface MeCapabilities {
  /** Gates the two create controls on the Projects list. */
  readonly create_project: boolean;
  readonly view_users: boolean;
  readonly invite_users: boolean;
  readonly edit_users: boolean;
  readonly revoke_invitations: boolean;
  readonly view_company: boolean;
  readonly edit_company: boolean;
  readonly view_audit: boolean;
  /** Separate from viewing: a Director reads the trail but cannot take a copy. */
  readonly export_audit: boolean;
  readonly view_templates: boolean;
  /** Company Admin alone (`template: g('VCED')`). */
  readonly manage_templates: boolean;
  /** Looking at what each role may do. A Director holds this and not the next. */
  readonly view_roles: boolean;
  /** Changing it. `role: admin`, which is Company Admin alone by default. */
  readonly manage_roles: boolean;
}

/**
 * Somebody from the vendor is looking at this company's data, right now.
 *
 * Null almost always. When it is not null the app draws a banner nobody can
 * miss — and it draws one for BOTH sides: `you_are_visiting` tells them apart.
 * The customer is told while it is happening rather than afterwards in an audit
 * trail, which was the user's choice when this was built.
 */
export interface SupportVisit {
  readonly operator_name: string;
  readonly reason: string;
  /** Whether this visit can see costs, budgets, variations and invoices. */
  readonly money_visible: boolean;
  readonly started_at: string;
  readonly expires_at: string;
  /** True for the vendor looking in; false for the customer being looked at. */
  readonly you_are_visiting: boolean;
}

export interface Me {
  readonly user_id: string;
  readonly company_id: string;
  readonly company_name: string | null;
  /** Null during a support visit — a visitor holds no role in this company. */
  readonly company_role: string | null;
  readonly finance_view: boolean;
  readonly can: MeCapabilities;
  readonly support?: SupportVisit | null;
}

export function me(): Promise<Me> {
  return request<Me>('/v1/me');
}

// --------------------------------------------------------- redeeming an invite

export interface InvitationSummary {
  readonly company_name: string;
  readonly expires_at: string;
}

export function describeInvitation(token: string): Promise<InvitationSummary> {
  return request<InvitationSummary>(`/v1/auth/invitation?token=${encodeURIComponent(token)}`);
}

export function acceptInvitation(input: {
  token: string;
  email: string;
  name: string;
  password: string;
}): Promise<{ user_id: string; company_id: string; signed_in: boolean }> {
  return request('/v1/auth/accept-invitation', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ------------------------------------------------------ redeeming a reset link

/**
 * The same shape as an invitation summary, and deliberately the same fields:
 * the company name proves the link is live without naming the person it is for.
 */
export type ResetSummary = InvitationSummary;

export function describePasswordReset(token: string): Promise<ResetSummary> {
  return request<ResetSummary>(`/v1/auth/password-reset?token=${encodeURIComponent(token)}`);
}

export function completePasswordReset(input: {
  token: string;
  email: string;
  password: string;
}): Promise<{ user_id: string; company_id: string; signed_in: boolean }> {
  return request('/v1/auth/complete-password-reset', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
